import React, { useEffect, useState } from 'react';
import styles from './my-downloads-grid.module.scss';
import FileZipIcon from 'src/assets/svg/file-zip-icon';
import UTEClientViewWrapper from 'src/components/ute-client-view-wrapper';
import UTEButton from 'src/components/ute-button';
import { DownloadIcon } from 'src/assets/svg/download-icon';
import RecycleBinIcon from 'src/assets/svg/recycle-bin-icon';
import { IMyDownloads, MyDownloadStatus } from '../my-downloads-types';
import { formatBytes, getFormattedDateAndTimeFromString } from 'src/modules/documents/documents-utility-functions';
import RetryIcon from 'src/assets/svg/retry-icon';
import { UTENotifier } from 'src/modules/notification/ute-notifier';
import { toast } from 'react-toastify';
import { DeviceType, useDeviceType } from 'src/hooks/useDeviceType';

type MyDownloadsGridProps = {
	data: IMyDownloads[];
	onDownloadFile: (storageAccountId: number, fileName: string, filePath: string) => void;
	onDeleteMyDownload: (myDownloadId: number) => void;
	onRetry: (myDownloadId: number) => void;
};

const MydownloadsGrid = ({ data, onDownloadFile, onDeleteMyDownload, onRetry }: MyDownloadsGridProps) => {
	const [toastId, setToastId] = useState<number>(0);
	const deviceType = useDeviceType();

	useEffect(() => {
		const processingFiles = data.filter((x) => x.status === MyDownloadStatus.Processing);
		if (processingFiles && processingFiles.length > 0 && !toast.isActive(toastId)) {
			const id = UTENotifier.Info('Download in progress. You can return later to check its status.', false);
			setToastId(id);
		}
	}, [data]);

	return (
		<div>
			<section className={styles.myDownloadsContainer}>
				<table>
					<tbody>
						{data.length == 0 ? <div className={styles.noDataContainer}>No data to display</div> : <></>}

						{data &&
							data.map((item, i) => (
								<tr className={styles.myDownloadsRow}>
									<td>
										<div className={styles.filenameContainer}>
											<div>
												<FileZipIcon />
											</div>
											<div>
												<div className={styles.filename}>{item.fileName}</div>
												<div className={styles.downloadInfo}>
													{item.status === MyDownloadStatus.Processing ? (
														<div className={styles.progressBarContainer}>
															<div className={styles.progressBar}></div>
														</div>
													) : item.status === MyDownloadStatus.Failed ? (
														<>{<span className={styles.errorText}>ZIP creation failed. Please try again.</span>}</>
													) : (
														<>
															{getFormattedDateAndTimeFromString(item.createdOn.toString())} - {item.fileCount} files -{' '}
															{formatBytes(item.fileSize)}
														</>
													)}
												</div>
											</div>
										</div>
									</td>
									<td className={styles.actionTd}>
										<div className={styles.actionContainer}>
											{item.status === MyDownloadStatus.Completed ? (
												<>
													<div>
														<UTEClientViewWrapper dataTestAuto='622D863C-8130-4D79-9567-7C73A5E012CA'>
															<UTEButton
																icon={React.createElement(DownloadIcon, { color: '#05386B' })}
																className={styles.btnDownload}
																dataTestAuto='95D9B1F9-DD30-47A7-A4B9-6B8D987EB793'
																text={`${deviceType === DeviceType.Desktop ? 'Download' : ''}`}
																onClick={() => {
																	onDownloadFile(item.storageAccountId, item.fileName, item.filePath);
																}}
																disabled={false}
																title='Download'
															/>
														</UTEClientViewWrapper>
													</div>
													<div>
														<UTEClientViewWrapper dataTestAuto='F58B6F17-729A-4140-8321-479D2BF5B4EE'>
															<UTEButton
																icon={React.createElement(RecycleBinIcon)}
																className={styles.btnDelete}
																dataTestAuto='3C029F5A-421F-4DEA-BF7F-E3ADDADAABF4'
																text={`${deviceType === DeviceType.Desktop ? 'Delete' : ''}`}
																onClick={() => {
																	onDeleteMyDownload(item.myDownloadsId);
																}}
																disabled={false}
																title='Delete'
															/>
														</UTEClientViewWrapper>
													</div>
												</>
											) : item.status === MyDownloadStatus.Failed ? (
												<>
													<div>
														<UTEClientViewWrapper dataTestAuto='F9FF65D7-870E-4CE6-8B65-E24CE4278CA9'>
															<UTEButton
																icon={React.createElement(RetryIcon)}
																className={styles.btnDelete}
																dataTestAuto='381ACC39-2CE1-467A-BF38-CBB3D3BB3CC9'
																text={`${deviceType === DeviceType.Desktop ? 'Retry' : ''}`}
																onClick={() => {
																	onRetry(item.myDownloadsId);
																}}
																disabled={false}
																title='Retry'
															/>
														</UTEClientViewWrapper>
													</div>
												</>
											) : (
												<></>
											)}
										</div>
									</td>
								</tr>
							))}
					</tbody>
				</table>
			</section>
		</div>
	);
};

export default MydownloadsGrid;
